import * as React from "react";
import { cn } from "@/lib/utils";
import { ChevronDownIcon, XIcon } from "@/components/icons";
import { inputClass } from "./input";
import { menuItemClass } from "./dropdown-menu";
import { Popover, PopoverAnchor, PopoverContent } from "./popover";

// MUI's Autocomplete, for the one shape the pane uses: pick one option from a list,
// filtered by what is typed, with the typed text controlled by the caller (so it can
// drive a server-side search too). Not a multi-select, not free text.
//
// Focus never leaves the input while the list is open: the list, the clear button and
// the chevron all swallow mousedown. That is what makes blur mean "the user left the
// field" and nothing else, so blur can close the list and put the text back to the
// selected option's label -- what MUI did, and what stops half-typed text sitting in a
// field that has a different value.

interface ComboboxProps<T> {
  id?: string;
  value: T | null;
  onChange: (value: T | null) => void;
  inputValue: string;
  onInputChange: (value: string) => void;
  options: T[];
  getOptionLabel: (option: T) => string;
  loading?: boolean;
  disabled?: boolean;
  placeholder?: string;
  name?: string;
  /** Escape on a closed list clears the field: MUI's `clearOnEscape`. */
  clearOnEscape?: boolean;
  /** An icon inside the field, before the text. */
  startIcon?: React.ReactNode;
  className?: string;
}

function Combobox<T>({
  id,
  value,
  onChange,
  inputValue,
  onInputChange,
  options,
  getOptionLabel,
  loading = false,
  disabled = false,
  placeholder,
  name,
  clearOnEscape = false,
  startIcon,
  className,
}: ComboboxProps<T>) {
  const generatedId = React.useId();
  const inputId = id ?? generatedId;
  const listId = `${inputId}-listbox`;
  const optionId = (index: number) => `${inputId}-option-${index}`;

  const anchorRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [open, setOpen] = React.useState(false);
  const [highlighted, setHighlighted] = React.useState(-1);
  // Whether the text has been typed since the list last opened. Until it has, the text
  // is the selected option's label and filtering by it would show only that option.
  const [dirty, setDirty] = React.useState(false);

  const selectedLabel = value !== null ? getOptionLabel(value) : null;
  const query = !dirty && inputValue === selectedLabel ? "" : inputValue;
  const filtered = React.useMemo(() => {
    const needle = query.toLowerCase();
    return needle
      ? options.filter((option) => getOptionLabel(option).toLowerCase().includes(needle))
      : options;
  }, [options, query, getOptionLabel]);

  const isSelected = (option: T) =>
    selectedLabel !== null && getOptionLabel(option) === selectedLabel;

  const openList = () => {
    if (disabled || open) return;
    setOpen(true);
    setDirty(false);
    // Opening on a chosen value puts the highlight on it, as a native select does.
    setHighlighted(selectedLabel === null ? -1 : options.findIndex(isSelected));
  };

  const closeList = () => {
    setOpen(false);
    setHighlighted(-1);
  };

  const select = (option: T) => {
    onChange(option);
    onInputChange(getOptionLabel(option));
    setDirty(false);
    closeList();
  };

  const clear = () => {
    onChange(null);
    onInputChange("");
    setDirty(false);
  };

  React.useEffect(() => {
    if (!open || highlighted < 0) return;
    // jsdom has no scrollIntoView.
    document.getElementById(optionId(highlighted))?.scrollIntoView?.({ block: "nearest" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, highlighted]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    switch (event.key) {
      case "ArrowDown":
      case "ArrowUp": {
        event.preventDefault();
        if (!open) {
          openList();
          return;
        }
        if (filtered.length === 0) return;
        const step = event.key === "ArrowDown" ? 1 : -1;
        setHighlighted((current) =>
          current < 0
            ? step > 0
              ? 0
              : filtered.length - 1
            : (current + step + filtered.length) % filtered.length
        );
        return;
      }
      case "Enter":
        // Never let Enter submit a surrounding form while choosing.
        if (open && highlighted >= 0 && highlighted < filtered.length) {
          event.preventDefault();
          select(filtered[highlighted]);
        }
        return;
      case "Escape":
        if (open) {
          // Close the list only, not whatever sheet or popover the field sits in.
          event.preventDefault();
          event.stopPropagation();
          closeList();
        } else if (clearOnEscape && (value !== null || inputValue)) {
          event.preventDefault();
          clear();
        }
        return;
      default:
        return;
    }
  };

  const keepFocus = (event: React.MouseEvent) => event.preventDefault();
  const showClear = !disabled && (value !== null || inputValue !== "");

  return (
    <Popover open={open && !disabled}>
      <PopoverAnchor asChild>
        <div
          ref={anchorRef}
          data-disabled={disabled ? "" : undefined}
          className={cn(
            inputClass,
            "group flex h-8 items-center gap-1 px-2 focus-within:border-primary focus-within:ring-1 focus-within:ring-primary data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50",
            className
          )}
        >
          {startIcon && <span className="flex shrink-0 text-muted-foreground">{startIcon}</span>}
          <input
            ref={inputRef}
            id={inputId}
            name={name}
            type="text"
            role="combobox"
            aria-expanded={open}
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={open && highlighted >= 0 ? optionId(highlighted) : undefined}
            autoComplete="off"
            placeholder={placeholder}
            disabled={disabled}
            value={inputValue}
            onChange={(event) => {
              onInputChange(event.target.value);
              setDirty(true);
              setHighlighted(-1);
              if (!open) setOpen(true);
            }}
            onMouseDown={openList}
            onKeyDown={handleKeyDown}
            onBlur={() => {
              closeList();
              setDirty(false);
              onInputChange(selectedLabel ?? "");
            }}
            className="h-full min-w-0 flex-1 bg-transparent outline-none placeholder:text-disabled disabled:cursor-not-allowed"
          />
          {showClear && (
            <button
              type="button"
              tabIndex={-1}
              aria-label="Clear"
              onMouseDown={keepFocus}
              onClick={clear}
              className="invisible flex size-6 shrink-0 items-center justify-center rounded-md text-muted-foreground group-focus-within:visible group-hover:visible hover:bg-accent"
            >
              <XIcon className="size-4" />
            </button>
          )}
          <button
            type="button"
            tabIndex={-1}
            aria-label={open ? "Close" : "Open"}
            disabled={disabled}
            onMouseDown={keepFocus}
            onClick={() => {
              inputRef.current?.focus();
              if (open) closeList();
              else openList();
            }}
            className="-mr-1 flex size-6 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-accent disabled:pointer-events-none"
          >
            <ChevronDownIcon className={cn("size-4 transition-transform", open && "rotate-180")} />
          </button>
        </div>
      </PopoverAnchor>
      <PopoverContent
        align="start"
        // The input keeps focus; the list is driven from its keyboard handler.
        onOpenAutoFocus={(event) => event.preventDefault()}
        onCloseAutoFocus={(event) => event.preventDefault()}
        onInteractOutside={(event) => {
          if (anchorRef.current?.contains(event.target as Node)) event.preventDefault();
          else closeList();
        }}
        onEscapeKeyDown={closeList}
        onMouseDown={keepFocus}
        className="max-h-[40vh] w-(--radix-popover-trigger-width) p-1"
      >
        <ul id={listId} role="listbox" aria-label={placeholder}>
          {filtered.length === 0 ? (
            <li className="px-2 py-1.5 text-body2 text-muted-foreground">
              {loading ? "Loading…" : "No options"}
            </li>
          ) : (
            filtered.map((option, index) => (
              <li
                key={`${getOptionLabel(option)}-${index}`}
                id={optionId(index)}
                role="option"
                aria-selected={isSelected(option)}
                data-highlighted={index === highlighted ? "" : undefined}
                onMouseMove={() => setHighlighted(index)}
                onClick={() => select(option)}
                className={cn(menuItemClass, "cursor-pointer py-1 aria-selected:bg-primary-tint")}
              >
                {getOptionLabel(option)}
              </li>
            ))
          )}
        </ul>
      </PopoverContent>
    </Popover>
  );
}

export { Combobox };
export type { ComboboxProps };
